import React, {Fragment} from 'react';
import PropTypes from 'prop-types';

import {isEmpty} from 'lodash';
import translate from '../../../utils/translations';
import classNames from 'classnames';
import {ALARM__24} from '@homeaway/svg-defs/lib/defs/ALARM__24';
import {AlertInfo} from '@homeaway/react-alerts';
import determineIfMobile from '@homeaway/react-determine-if-mobile';
import Experiment from '@homeaway/react-experiment';
import URLSearchParams from '@ungap/url-search-params';

/* Molecules */

import {HelpCenter} from '../HelpCenter/HelpCenter';
import {PolicySection} from '../PolicySection/PolicySection';
import {PropertyCarousel} from '../PropertyCarousel/PropertyCarousel';
import {PropertyContact} from '../PropertyContact/PropertyContact';
import {PropertyContactMobile} from '../PropertyContactMobile/PropertyContactMobile';
import {PropertyReview} from '../PropertyReview/PropertyReview';
import {RefundInfo} from '../RefundInfo/RefundInfo';
import {RefundVAWrapper} from '../RefundVAWrapper/RefundVAWrapper';
import {ServiceFeeSummary} from '../ServiceFeeSummary/ServiceFeeSummary';
import {TripSummary} from '../TripSummary/TripSummary';
import {IpmHostInfo} from '../IpmHostInfo/IpmHostInfo';
import {
    TRIP_DETAILS,
    tripDetailsPresented,
} from '../../../utils/EDAP/ConversationDetails';
import {TripSummaryCard} from '../TripSummaryCard/TripSummaryCard';
import {TripDetailsInfobox} from '../TripDetailsInfobox/TripDetailsInfobox';
import {withErrorBoundary} from '../../ErrorBoundary/ErrorBoundary';

import InviteGuestsEntryModal from '../InviteGuestsEntryModal/InviteGuestsEntryModal';
import GuestsOfGuest from '../../organisms/GuestsOfGuest/GuestsOfGuest';

import './ConversationDetails.less';

export class ConversationDetails extends React.Component {
    static propTypes = {
        arrivalTime: PropTypes.string,
        backUrl: PropTypes.string,
        contact: PropTypes.shape({
            memberSince: PropTypes.string,
            name: PropTypes.string,
            ownerProfilePhoto: PropTypes.string,
        }),
        conversationId: PropTypes.string,
        countdownSummary: PropTypes.shape({}),
        departureTime: PropTypes.string,
        fullPhoneNumber: PropTypes.string,
        fullPhoneDigits: PropTypes.string,
        geoCode: PropTypes.shape({}),
        globalMessages: PropTypes.shape({}),
        isPrimaryGuest: PropTypes.bool,
        helpUrl: PropTypes.string,
        policies: PropTypes.arrayOf(PropTypes.shape({})),
        priceDetails: PropTypes.shape({}),
        propertyId: PropTypes.number,
        propertyImage: PropTypes.string,
        propertyUrl: PropTypes.string,
        quoteTotals: PropTypes.arrayOf(PropTypes.shape({})),
        refetch: PropTypes.func,
        reservationId: PropTypes.string,
        responseTime: PropTypes.string,
        reservationUuid: PropTypes.string,
        reviewFormUrl: PropTypes.string,
        serviceFeeSummary: PropTypes.shape({}),
        showRefundInfoBtn: PropTypes.bool,
        showSendMessageBtn: PropTypes.bool,
        systemData: PropTypes.shape({}),
        travelerStayData: PropTypes.shape({}),
        tripSummary: PropTypes.shape({
            arrivalDate: PropTypes.string,
            departureDate: PropTypes.string,
        }),
    };

    static defaultProps = {
        backUrl: '/traveler/th/inbox',
        contact: {},
        countdownSummary: {},
        refetch: () => {},
        systemData: {},
        travelerStayData: {},
        tripSummary: {},
    };

    componentDidMount() {
        const {reservationUuid, search} = this.props;
        const params = new URLSearchParams(search);
        tripDetailsPresented({
            actionLocation: TRIP_DETAILS,
            reservationUuid,
            redirectSource: params.get('redirectSource'),
            redirectSourceType: params.get('redirectSourceType'),
        });
    }

    getHelpCenter() {
        const {systemData, translations} = this.props;
        return (
            <HelpCenter
                humanReadableBrandName={systemData.site?.humanReadableBrandName}
                translations={translations}
            />
        );
    }

    getPolicySection() {
        const {
            backUrl,
            conversationId,
            isPrimaryGuest,
            listingId,
            location,
            policies,
            priceDetails,
            refetch,
            reservationUuid,
            contact,
            showRefundInfoBtn,
            systemData,
            translations,
        } = this.props;

        return (
            <Fragment>
                <PolicySection
                    backUrl={backUrl}
                    conversationId={conversationId}
                    isPrimary={isPrimaryGuest}
                    listingId={listingId}
                    location={location}
                    policies={policies}
                    priceDetails={priceDetails}
                    refetch={refetch}
                    reservationId={reservationUuid}
                    shortDisplayName={contact.name}
                    showRefundInfoBtn={showRefundInfoBtn}
                    systemData={systemData}
                    translations={translations}
                />
            </Fragment>
        );
    }

    render() {
        const {
            adults,
            arrival,
            backUrl,
            children,
            contact,
            conversationId,
            countdownSummary,
            departure,
            fullPhoneDigits,
            fullPhoneNumber,
            globalMessages,
            gogInvitePopup,
            handelBannerClick,
            isGuestModalOpen,
            isMobile,
            isPrimaryGuest,
            ownerAvatar,
            priceDetails,
            propertyId,
            propertyImage,
            propertyImageC5,
            reservationId,
            reservationUuid,
            responseTime,
            reviewFormUrl,
            serviceFeeSummary,
            showRefundInfoBtn,
            showSendMessageBtn,
            systemData,
            translations,
            tripSummary,
        } = this.props;
        const prices = priceDetails || {};
        const rightRailClassNames = classNames(
            'col-xs-12',
            'col-sm-5',
            'col-sm-push-7',
            'col-md-4',
            'col-md-push-8',
            'details-section__col--right',
            'details-section__col--rightRailReorder'
        );

        return (
            <div className="ConversationDetails details-section row">
                {showRefundInfoBtn && <RefundVAWrapper />}
                {systemData && systemData.isTablet && (
                    <span className="Infobox-tablet col-xs-12">
                        <TripDetailsInfobox globalMessages={globalMessages} />
                    </span>
                )}
                {tripSummary.conversationLifecycleState ===
                    'RESERVATION_REQUEST' && (
                    <div
                        className={classNames('col-xs-12 margin-top-6x', {
                            'margin-top-4x margin-bottom-zero':
                                systemData.isDesktop,
                        })}
                    >
                        <AlertInfo
                            msg={translate(
                                translations,
                                'trav-dash/trav-home',
                                'messages',
                                'th.inbox.conversation.alert.bookingRequest.details'
                            )}
                            svgMarkup={ALARM__24}
                            title={translate(
                                translations,
                                'trav-dash/trav-home',
                                'messages',
                                'th.inbox.conversation.alert.bookingRequest',
                                {0: contact.name}
                            )}
                        />
                    </div>
                )}

                {reviewFormUrl?.length > 0 && (
                    <div
                        className={classNames('hidden-xs col-xs-12', {
                            'margin-top-4x margin-bottom-zero':
                                systemData.isDesktop,
                        })}
                    >
                        <PropertyReview
                            checkoutDate={tripSummary.departureDate}
                            conversationId={conversationId}
                            reservationId={reservationUuid}
                            reviewFormUrl={reviewFormUrl}
                            seenEvent
                            translations={translations}
                        />
                    </div>
                )}

                <div className={rightRailClassNames}>
                    {!isEmpty(contact) && (
                        <span>
                            {systemData.isTablet && (
                                <PropertyContact
                                    avatar={contact.ownerProfilePhoto}
                                    backUrl={backUrl}
                                    conversationId={conversationId}
                                    memberSince={contact.memberSince}
                                    ownerName={contact.name}
                                    phone={fullPhoneNumber}
                                    showIpmHostInfo={false}
                                    showSendMessageBtn={showSendMessageBtn}
                                    translations={translations}
                                />
                            )}
                            <PropertyCarousel image={propertyImage} />
                            {reviewFormUrl?.length > 0 && (
                                <div className="visible-xs-block">
                                    <PropertyReview
                                        checkoutDate={tripSummary.departureDate}
                                        conversationId={conversationId}
                                        reservationId={reservationUuid}
                                        reviewFormUrl={reviewFormUrl}
                                        translations={translations}
                                    />
                                </div>
                            )}
                            {isMobile && (
                                <Fragment>
                                    <span className="Advisory__Infobox_mobile">
                                        <TripDetailsInfobox
                                            globalMessages={globalMessages}
                                        />
                                    </span>
                                    {showRefundInfoBtn && (
                                        <RefundInfo
                                            translations={translations}
                                        />
                                    )}
                                </Fragment>
                            )}
                            {isMobile && (
                                <PropertyContactMobile
                                    avatar={contact.ownerProfilePhoto}
                                    backUrl={backUrl}
                                    conversationId={conversationId}
                                    ownerName={contact.name}
                                    phone={fullPhoneDigits}
                                    showSendMessageBtn={showSendMessageBtn}
                                    translations={translations}
                                />
                            )}
                        </span>
                    )}

                    <TripSummary
                        actionLocation={TRIP_DETAILS}
                        backUrl={backUrl}
                        expirationDateTime={countdownSummary.expirationDateTime}
                        translations={translations}
                    />

                    <Experiment name={'GoG_removal_Vrbex'} variant={'off'}>
                        {gogInvitePopup &&
                            withErrorBoundary(
                                <InviteGuestsEntryModal
                                    actionLocation={TRIP_DETAILS}
                                    adults={adults}
                                    children={children}
                                    conversationId={conversationId}
                                    isPrimary={isPrimaryGuest}
                                    reservationArrival={arrival}
                                    reservationDeparture={departure}
                                    reservationUUID={reservationUuid}
                                    translations={translations}
                                />,
                                'invite-guests-entry-modal'
                            )}
                        {withErrorBoundary(
                            <GuestsOfGuest
                                actionLocation={TRIP_DETAILS}
                                conversationId={conversationId}
                                isOpen={isGuestModalOpen}
                                isPrimary={isPrimaryGuest}
                                reservationUuid={reservationUuid}
                                translations={translations}
                            />,
                            'guests-of-guest'
                        )}
                    </Experiment>
                    {isMobile && this.getPolicySection()}
                    <ServiceFeeSummary
                        checkoutUrl={prices.checkoutUrl}
                        serviceFeeSummary={serviceFeeSummary}
                    />

                    {(systemData.isDesktop || systemData.isTablet) &&
                        this.getHelpCenter()}
                </div>

                <div className="col-xs-12 col-sm-7 col-sm-pull-5 col-md-8 col-md-pull-4 details-section__col--left">
                    {systemData && systemData.isDesktop && (
                        <div className="Advisory__Infobox">
                            <TripDetailsInfobox
                                globalMessages={globalMessages}
                            />
                        </div>
                    )}
                    {!isMobile && showRefundInfoBtn && (
                        <RefundInfo translations={translations} />
                    )}
                    {systemData.isDesktop && (
                        <TripSummaryCard
                            actionLocation={TRIP_DETAILS}
                            backUrl={backUrl}
                            contact={contact}
                            conversationId={conversationId}
                            expirationDateTime={
                                countdownSummary.expirationDateTime
                            }
                            fullPhoneNumber={fullPhoneNumber}
                            handelBannerClick={handelBannerClick}
                            ownerAvatar={ownerAvatar}
                            propertyId={propertyId}
                            propertyImageC5={propertyImageC5}
                            propertyUrl={this.props.propertyUrl}
                            quoteTotals={this.props.quoteTotals}
                            reservation={reservationId}
                            reservationUuid={reservationUuid}
                            responseTime={responseTime}
                            showIpmHostInfo
                            showSendMessageBtn={showSendMessageBtn}
                            translations={translations}
                        />
                    )}

                    {systemData.isTablet && (
                        <IpmHostInfo translations={translations} />
                    )}
                    {!isMobile && this.getPolicySection()}
                    {isMobile && this.getHelpCenter()}
                </div>
            </div>
        );
    }
}

const conversationDetailsIsMobileCheck = determineIfMobile(ConversationDetails);

export {conversationDetailsIsMobileCheck as ConversationDetailsDetermineIsMobile};
